import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import { getTripCatalogManifest, getCatalogManifest, getTripMeta } from '@/lib/api';
import { resolveStayCoords } from '@/lib/dest-coords';
import type { CatalogManifestEntry } from '@/lib/types';
import {
  KIND_LABELS as KIND_LABEL,
  applyStopwords,
  rankDocs,
  tokenize,
} from '@core/search-core';
import { docOfManifestEntry } from '@/lib/search-docs';
import { priceLabelOf } from '@/lib/catalog-derive';
import { placeLabel } from '@/lib/humanize';
import { useListNav } from '@/lib/use-list-nav';
import type { SearchDoc } from '@core/search-core';

// Global search modal (⌘K) — overlay over the current view, single ranked
// list across all catalog kinds, keyboard-first: arrows move selection,
// Enter activates, Esc closes, click-outside closes.
//
// Matching + ranking come from the shared core (src/lib/search-core.js) —
// weighted field scoring (name > aliases > region > tags > detail), multi-
// word AND with stopword stripping, diacritic folding, 1-edit fuzzy for
// 5+ char tokens. Don't add scoring rules here; add them to the core so
// mobile / MapMode / Browse / the Worker stay in lockstep.
//
// Index source is the trip-scoped manifest (same data Browse's default
// scope shows) — the global manifest is ~12k entries / ~14 MB now, too
// heavy to pull on modal-open. Cached per trip for the session.

// Cross-kind dedupe priority — when the same place exists in multiple
// catalog kinds (e.g. a kaiseki ryokan that is both Hotel and Restaurant),
// collapse to one row at the kind earlier in this list; runner-up kinds
// show as a "+ Kind" note on the row.
const KIND_PRIORITY = ['hotels', 'attractions', 'restaurants', 'experiences', 'shopping', 'bars', 'destinations'];

interface IndexRow {
  type: string;
  id: string;
  name: string;
  region: string;
  subregion: string;
  alsoKinds: string[];
  entry: CatalogManifestEntry;
  doc: SearchDoc;
}

function buildIndex(entries: CatalogManifestEntry[]): IndexRow[] {
  const flat: IndexRow[] = [];
  for (const e of entries) {
    if (!e?.id || !KIND_LABEL[e.type]) continue;
    flat.push({
      type: e.type,
      id: e.id,
      name: e.name || e.id,
      region: e.region || '',
      subregion: e.subregion || '',
      alsoKinds: [],
      entry: e,
      doc: docOfManifestEntry(e),
    });
  }
  // Dedupe by normalized name (the only field kept identical across a
  // place's entries in different kinds). Canonical row = earliest-priority
  // kind; other kinds become alsoKinds badges; same-kind dupes drop silently.
  const groups = new Map<string, IndexRow[]>();
  for (const r of flat) {
    const g = groups.get(r.doc._name);
    if (g) g.push(r);
    else groups.set(r.doc._name, [r]);
  }
  const drop = new Set<IndexRow>();
  for (const list of groups.values()) {
    if (list.length < 2) continue;
    list.sort((a, b) => KIND_PRIORITY.indexOf(a.type) - KIND_PRIORITY.indexOf(b.type));
    const canonical = list[0];
    const seen = new Set([canonical.type]);
    for (let i = 1; i < list.length; i++) {
      const r = list[i];
      if (!seen.has(r.type)) {
        canonical.alsoKinds.push(KIND_LABEL[r.type]);
        seen.add(r.type);
      }
      drop.add(r);
    }
  }
  return flat.filter((r) => !drop.has(r));
}

// ── ranking (shared core — src/lib/search-core.js) ────────────────────────────

// Cap at 25 — the long tail is mostly fuzzy / detail-field dross.
const RESULT_CAP = 25;

// On a trip, a place near one of its stays ranks above a same-scoring
// place elsewhere, and one far from every stay drops ("tempura omakase"
// led with Honolulu and Chicago on the Japan trip).
type StayPoint = { lat: number; lng: number };
function tripBoost(e: CatalogManifestEntry, stays: StayPoint[]): number {
  if (!stays.length || !e.coords) return 0;
  let best = Infinity;
  for (const p of stays) {
    const dLat = ((p.lat - e.coords.lat) * Math.PI) / 180;
    const dLng = ((p.lng - e.coords.lng) * Math.PI) / 180;
    const h = Math.sin(dLat / 2) ** 2 + Math.cos((e.coords.lat * Math.PI) / 180) * Math.cos((p.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
    best = Math.min(best, 12742 * Math.asin(Math.sqrt(h)));
  }
  return best < 60 ? 40 : best > 400 ? -80 : 0;
}

function runSearch(index: IndexRow[], tokens: string[], stays: StayPoint[] = []): IndexRow[] {
  if (!tokens.length) return [];
  const hits = rankDocs(index, tokens, (r) => r.doc).map((h) => ({ row: h.row, score: h.score + tripBoost(h.row.entry, stays) }));
  hits.sort((a, b) => b.score - a.score);
  return hits.slice(0, RESULT_CAP).map((h) => h.row);
}

// ── match highlighting (React nodes instead of <mark> innerHTML) ──────────

function highlight(text: string, tokens: string[]): ReactNode {
  if (!text || !tokens.length) return text;
  const escaped = tokens
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  if (!escaped.length) return text;
  const re = new RegExp(`(${escaped.join('|')})`, 'gi');
  const parts = text.split(re);
  return parts.map((p, i) =>
    i % 2 === 1 ? (
      <mark key={i} style={{ background: 'var(--accent-wash)', color: 'var(--accent-deep)', borderRadius: 2 }}>
        {p}
      </mark>
    ) : (
      p
    ),
  );
}

// ── chips ─────────────────────────────────────────────────────────────────

function chipsOf(e: CatalogManifestEntry): Array<{ label: string; accent?: boolean }> {
  const chips: Array<{ label: string; accent?: boolean }> = [];
  if (e.michelinStars && e.michelinStars > 0) chips.push({ label: '★'.repeat(e.michelinStars), accent: true });
  else if (e.michelinPlate) chips.push({ label: 'Plate', accent: true });
  if (e.tabelogScore != null) chips.push({ label: `Tabelog ${e.tabelogScore.toFixed(2)}` });
  if (e.googleScore != null) chips.push({ label: `Google ${e.googleScore.toFixed(1)}` });
  if (e.reservationPolicy) {
    const p = e.reservationPolicy;
    const label =
      p === 'required' ? 'Reservation required' :
      p === 'recommended' ? 'Reservation recommended' :
      p === 'not_accepted' ? 'Walk-in only' :
      'Walk-ins welcome';
    chips.push({ label, accent: p === 'required' });
  }
  // The tier in the place's own currency, not the raw price prose.
  const price = priceLabelOf(e, e.type);
  if (price) chips.push({ label: price });
  return chips.slice(0, 3);
}

// ── modal ─────────────────────────────────────────────────────────────────

// Session cache: trip-scoped manifest per trip, plus the whole-catalog
// index under a reserved key for the `global` (no-trip) search surface.
const indexCache = new Map<string, IndexRow[]>();
const GLOBAL_KEY = '__global__';

export default function SearchModal({
  tripId,
  initialQuery,
  onClose,
  global = false,
}: {
  // The trip results navigate into (/trip/{tripId}/catalog/...). Required —
  // even the global search needs a trip to anchor the detail/save surface.
  tripId: string;
  initialQuery?: string;
  onClose: () => void;
  // When true, index the whole catalog (getCatalogManifest) instead of the
  // trip-scoped manifest — used by the app-shell search on the non-trip pages
  // (/, /new, /reservations) so search reaches every entry, not one trip's scope.
  global?: boolean;
}) {
  const navigate = useNavigate();
  const cacheKey = global ? GLOBAL_KEY : tripId;
  const [index, setIndex] = useState<IndexRow[] | null>(indexCache.get(cacheKey) ?? null);
  const [loadError, setLoadError] = useState(false);
  const [q, setQ] = useState(initialQuery ?? '');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (index) return;
    let cancelled = false;
    (global ? getCatalogManifest() : getTripCatalogManifest(tripId))
      .then((m) => {
        if (cancelled) return;
        const built = buildIndex(m.entries ?? []);
        indexCache.set(cacheKey, built);
        setIndex(built);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [cacheKey, global, tripId, index]);

  // Body scroll lock while open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  const [stays, setStays] = useState<StayPoint[]>([]);
  useEffect(() => {
    if (global) return;
    let cancelled = false;
    getTripMeta(tripId)
      .then((meta) => resolveStayCoords(meta.stays ?? []).then((m) => ({ meta, m })))
      .then(({ meta, m }) => {
        if (cancelled) return;
        const pts: StayPoint[] = [];
        for (const st of meta.stays ?? []) {
          const c = st.destId ? m.get(st.destId) : undefined;
          if (c) pts.push({ lat: c[1], lng: c[0] });
        }
        setStays(pts);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [global, tripId]);

  const tokens = useMemo(() => applyStopwords(tokenize(q)), [q]);
  const results = useMemo(() => (index ? runSearch(index, tokens, stays) : []), [index, tokens, stays]);

  const activate = (row: IndexRow) => {
    onClose();
    navigate(`/trip/${tripId}/catalog/${row.type}/${encodeURIComponent(row.id)}`);
  };

  // Arrow/Enter/Escape handling is the shared hook every web search surface
  // uses — see lib/use-list-nav.ts. It also owns selection reset (on a new
  // query) and scroll-into-view.
  const nav = useListNav<HTMLUListElement>({
    count: results.length,
    resetKey: q,
    onActivate: (i) => activate(results[i]),
    onEscape: onClose,
  });
  const { index: selIdx, listRef } = nav;

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      onKeyDown={nav.onKeyDown}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 200,
        background: 'rgba(28, 24, 19, 0.35)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        paddingTop: '12vh',
      }}
    >
      <div
        role="dialog"
        aria-label="Search"
        style={{
          width: 'min(620px, calc(100vw - 32px))',
          maxHeight: '64vh',
          display: 'flex',
          flexDirection: 'column',
          background: 'var(--surface)',
          borderRadius: 14,
          border: '1px solid var(--line)',
          boxShadow: '0 24px 64px rgba(28, 24, 19, 0.28)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '0 16px',
            height: 52,
            borderBottom: '1px solid var(--line)',
            flexShrink: 0,
          }}
        >
          <Icon name="search" size={16} style={{ color: 'var(--ink-mute)', flexShrink: 0 }} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search hotels, places, restaurants…"
            spellCheck={false}
            autoComplete="off"
            style={{
              flex: 1,
              minWidth: 0,
              fontSize: 15,
              color: 'var(--ink)',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              fontFamily: 'inherit',
            }}
          />
          <span style={{ fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--ink-mute)', flexShrink: 0 }}>
            esc to close
          </span>
        </div>
        <ul
          ref={listRef}
          role="listbox"
          style={{ listStyle: 'none', margin: 0, padding: 6, overflowY: 'auto', flex: 1 }}
        >
          {loadError ? (
            <li style={{ padding: '28px 16px', textAlign: 'center', fontSize: 13, color: 'var(--ink-mute)' }}>
              Couldn't load the catalog — close and retry.
            </li>
          ) : !index ? (
            <li style={{ padding: '28px 16px', textAlign: 'center', fontSize: 13, color: 'var(--ink-mute)' }}>
              Loading the catalog…
            </li>
          ) : results.length === 0 ? (
            <li style={{ padding: '28px 16px', textAlign: 'center' }}>
              {q.trim() ? (
                <>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>No results</div>
                  <div style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>Try a name, region, or tag.</div>
                </>
              ) : (
                <div style={{ fontSize: 12.5, color: 'var(--ink-mute)' }}>
                  Search across hotels, restaurants, bars, attractions, experiences, shopping, and destinations.
                  Typos are okay.
                </div>
              )}
            </li>
          ) : (
            results.map((row, i) => {
              const sel = i === selIdx;
              const sub = [placeLabel(row.region), placeLabel(row.subregion)]
                .filter((x, k, all) => x && all.indexOf(x) === k)
                .join(' · ');
              const chips = chipsOf(row.entry);
              return (
                <li
                  key={`${row.type}:${row.id}`}
                  role="option"
                  {...nav.rowProps(i)}
                  onClick={() => activate(row)}
                  style={{
                    display: 'flex',
                    gap: 12,
                    alignItems: 'baseline',
                    padding: '9px 12px',
                    borderRadius: 9,
                    cursor: 'pointer',
                    background: sel ? 'var(--accent-wash)' : 'transparent',
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontFamily: 'var(--font-mono)',
                      color: 'var(--ink-mute)',
                      width: 78,
                      flexShrink: 0,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {KIND_LABEL[row.type]}
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: 'var(--ink)' }}>
                      {highlight(row.name, tokens)}
                      {row.alsoKinds.length > 0 && (
                        <span
                          title={`Also categorized as ${row.alsoKinds.join(', ')}`}
                          style={{ marginLeft: 8, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--ink-mute)', fontWeight: 400 }}
                        >
                          + {row.alsoKinds.join(' · ')}
                        </span>
                      )}
                    </span>
                    {sub && (
                      <span style={{ display: 'block', fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--ink-mute)', marginTop: 1 }}>
                        {highlight(sub, tokens)}
                      </span>
                    )}
                    {chips.length > 0 && (
                      <span style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                        {chips.map((c, k) => (
                          <span
                            key={k}
                            style={{
                              fontSize: 11,
                              fontFamily: 'var(--font-mono)',
                              padding: '1px 7px',
                              borderRadius: 99,
                              border: '1px solid var(--line)',
                              color: c.accent ? 'var(--accent-deep)' : 'var(--ink-mute)',
                              background: c.accent ? 'var(--accent-wash)' : 'transparent',
                            }}
                          >
                            {c.label}
                          </span>
                        ))}
                      </span>
                    )}
                  </span>
                </li>
              );
            })
          )}
        </ul>
      </div>
    </div>
  );
}
